import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Tooltip, Typography } from '@material-ui/core';
import InfoIcon from '@material-ui/icons/Info';
import { Card, Table } from 'components/UI';
import Paginator from 'components/Paginator';
import Loading from 'components/Loading';
import { useStyles, customCardStyles } from './Statuses.styled';
import { vehicleActions } from 'state/modules/vehicles';
import i18n from 'i18n-js';
import moment from 'moment';

const Statuses = ({ id }) => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const cardClasses = customCardStyles();

  const {
    loadingVehicleStatuses,
    vehicleStatuses,
    vehicleStatusesPagination,
    fetchedChangeStatusSuccess,
  } = useSelector((state) => state.vehicles);

  const [params, setParams] = useState({
    page: 1,
    limit: 5,
  });

  const fetchVehicleStatuses = useCallback(() => {
    if (id) {
      dispatch(vehicleActions.fetchVehicleStatusesById(id, params));
    }
  }, [id, params]);

  useEffect(() => {
    if (fetchedChangeStatusSuccess) {
      fetchVehicleStatuses();
    }
  }, [fetchedChangeStatusSuccess]);

  useEffect(() => {
    fetchVehicleStatuses();
  }, [params]);

  const handlePageChange = (event, page) => {
    const newParams = { page: page + 1, limit: params.limit };
    setParams({ ...params, ...newParams });
  };

  /**
   * Vertical menu list
   * It has the label and the function for each action
   */
  const items = [
    {
      title: i18n.t('refresh'),
      onClick: () => {
        const newParams = { ...params, page: 1 };
        setParams(newParams);
      },
    },
  ];

  /**
   * Table columns
   */
  const columns = [
    {
      grow: 1,
      name: i18n.t('vehicle.statuses.status'),
      selector: 'status',
      cell: (row) => i18n.t(`vehicleStatus.${row.status}`),
    },
    {
      grow: 1,
      name: i18n.t('vehicle.statuses.start_date'),
      selector: 'start_time',
      cell: (row) =>
        row?.start_time ? (
          <Box display="flex" flexDirection="column">
            <div>{moment(row?.start_time).format('MM/DD/YY')}</div>
            <div>{moment(row?.start_time).format('hh:mm A')}</div>
          </Box>
        ) : (
          ''
        ),
    },
    {
      grow: 1,
      name: i18n.t('vehicle.statuses.end_date'),
      selector: 'end_time',
      cell: (row) =>
        row?.end_time ? (
          <Box display="flex" flexDirection="column">
            <div>{moment(row?.end_time).format('MM/DD/YY')}</div>
            <div>{moment(row?.end_time).format('hh:mm A')}</div>
          </Box>
        ) : (
          ''
        ),
    },
    {
      name: i18n.t('vehicle.statuses.reason'),
      grow: 1,
      selector: 'reason',
      minWidth: '7rem',
      wrap: true,
      cell: (row) => (row?.reason ? i18n.t(`reasons.${row.reason}`) : ''),
    },
    {
      grow: 10,
      name: i18n.t('vehicle.statuses.description'),
      minWidth: '400px',
      selector: 'description',
      cell: (row) => row?.description || '',
    },
  ];

  const title = (
    <Box display="flex" alignItems="center">
      <Typography color="textPrimary" component="span">
        <Box fontSize={14} fontWeight="fontWeightMedium" data-cy="vehicle-statuses-title">
          {i18n.t('vehicle.statuses.title')}
        </Box>
      </Typography>
      <Tooltip
        title={i18n.t('vehicle.statuses.tooltip')}
        placement="right"
        classes={{ tooltip: classes.tooltip }}
      >
        <InfoIcon fontSize="small" className={classes.helpIconInline} />
      </Tooltip>
    </Box>
  );

  return (
    <Card
      title={title}
      menuItems={items}
      headerActionType="vertical"
      data-cy="vehicle-statuses-widget-card"
    >
      {!loadingVehicleStatuses ? (
        <Box justifyContent="center" alignItems="center" display="flex">
          <Loading />
        </Box>
      ) : (
        <>
          <Table
            columns={columns}
            pagination={false}
            data={vehicleStatuses}
            paginationComponentOptions={{ noRowsPerPage: true }}
          />
          <Paginator
            component="div"
            limit={params.limit}
            onPageChange={handlePageChange}
            pagination={vehicleStatusesPagination}
          />
        </>
      )}
    </Card>
  );
};

export default Statuses;
